/** Relative + docDate labels — plan/35 (under-absolute never prints a second calendar date). */

export type RelativeTimeMode = "standalone" | "under-absolute";

const MANILA = "Asia/Manila";

function manilaYmd(ms: number): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: MANILA,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date(ms));
}

function parseYmd(ymd: string): { y: number; m: number; d: number } | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return null;
  const [y, m, d] = ymd.split("-").map(Number);
  if (!y || !m || !d) return null;
  return { y, m, d };
}

/**
 * Age parts from `fromYmd` → `toYmd` using day-count (30d ≈ 1mo) for stable
 * compound labels — plan/35 dense `Xmo Yd` / `Xy Xmo`.
 */
export function calendarAgeParts(
  fromYmd: string,
  toYmd: string,
): { years: number; months: number; days: number; dayCount: number } | null {
  if (!parseYmd(fromYmd) || !parseYmd(toYmd)) return null;
  const dayCount = Math.round(
    (Date.parse(`${toYmd}T12:00:00Z`) - Date.parse(`${fromYmd}T12:00:00Z`)) /
      86_400_000,
  );
  if (dayCount < 0) {
    return { years: 0, months: 0, days: 0, dayCount };
  }
  const years = Math.floor(dayCount / 365);
  const remAfterYears = dayCount - years * 365;
  const months = Math.floor(remAfterYears / 30);
  const days = remAfterYears - months * 30;
  return { years, months, days, dayCount };
}

function formatUnderAbsoluteAge(parts: {
  years: number;
  months: number;
  days: number;
  dayCount: number;
}): string {
  const { years, months, days, dayCount } = parts;
  if (dayCount < 0) return "";
  if (dayCount === 0) return "0d";
  // Through 27 calendar days → Nd (plan/35).
  if (years === 0 && months === 0) {
    return `${Math.max(1, dayCount)}d`;
  }
  if (years === 0) {
    if (days === 0) return `${months}mo`;
    return `${months}mo ${days}d`;
  }
  if (months === 0) return `${years}y`;
  return `${years}y ${months}mo`;
}

function shortStandaloneDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-PH", {
    timeZone: MANILA,
    month: "short",
    day: "numeric",
  });
}

/**
 * Short relative time.
 * - `standalone` (comments): m/h/d then short calendar date.
 * - `under-absolute` (under Date column): never a calendar date — compound mo/y ladder.
 */
export function relativeTimeLabel(
  iso: string,
  nowMs = Date.now(),
  mode: RelativeTimeMode = "standalone",
): string {
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return "";
  const diffSec = Math.round((nowMs - t) / 1000);
  if (diffSec < 0) return "";
  if (diffSec < 45) return "just now";
  if (diffSec < 3600) return `${Math.max(1, Math.round(diffSec / 60))}m`;
  if (diffSec < 86400) return `${Math.max(1, Math.round(diffSec / 3600))}h`;

  if (mode === "under-absolute") {
    const fromYmd = manilaYmd(t);
    const toYmd = manilaYmd(nowMs);
    const parts = calendarAgeParts(fromYmd, toYmd);
    if (!parts) return "";
    // Same calendar day already handled via hours above when using wall clock ISO.
    // If still same day (edge), show hours path already returned.
    if (parts.dayCount === 0) {
      return `${Math.max(1, Math.round(diffSec / 3600))}h`;
    }
    if (parts.dayCount < 28 && parts.years === 0 && parts.months === 0) {
      return `${parts.dayCount}d`;
    }
    return formatUnderAbsoluteAge(parts);
  }

  if (diffSec < 86400 * 7) {
    return `${Math.max(1, Math.round(diffSec / 86400))}d`;
  }
  return shortStandaloneDate(iso);
}

/** Normalize API date wire (YYYY-MM-DD or ISO) to calendar YYYY-MM-DD. */
export function asCalendarYmd(raw: string | null | undefined): string {
  if (!raw) return "";
  return String(raw).trim().slice(0, 10);
}

/** Absolute short label for a calendar docDate — plan/35. */
export function formatDocDateLabel(raw: string | null | undefined): string {
  const ymd = asCalendarYmd(raw);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return raw?.trim() || "—";
  return new Date(`${ymd}T12:00:00Z`).toLocaleDateString("en-PH", {
    timeZone: "UTC",
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

/** Relative age of a calendar docDate (start of day, Asia/Manila) — under absolute. */
export function docDateRelativeLabel(
  raw: string | null | undefined,
  nowMs = Date.now(),
): string {
  const ymd = asCalendarYmd(raw);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return "";
  const parts = calendarAgeParts(ymd, manilaYmd(nowMs));
  if (!parts) return "";
  if (parts.dayCount < 0) return "";
  if (parts.dayCount === 0) {
    // Age from Manila midnight of docDate.
    return relativeTimeLabel(`${ymd}T00:00:00+08:00`, nowMs, "under-absolute");
  }
  if (parts.dayCount < 28 && parts.years === 0 && parts.months === 0) {
    return `${parts.dayCount}d`;
  }
  return formatUnderAbsoluteAge(parts);
}
